import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Mail, Lock, User, ArrowRight, ArrowLeft, Loader2, MapPin, Search,
  Building2, Trees, Compass, Check, X, ShoppingBag, Briefcase, Euro,
} from 'lucide-react';
import { COUNTRIES } from '../lib/countries';
import CountryInvestModal from '../components/CountryInvestModal';
import { GoogleAuthButton } from '../components/GoogleAuthButton';
import { InvestModal } from '../components/InvestLanguageFlags';
import ShopTypeChooser from '../components/ShopTypeChooser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_STYLE = `${process.env.REACT_APP_BACKEND_URL}/api/map/style`;
const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

const LATVIA_CENTER = [24.6, 56.88];
const LATVIA_BOUNDS = [[20.95, 55.65], [28.25, 58.10]];

const REGION_META = {
  kurzeme: { blurb: 'Rietumkrasts, jūra un priežu meži' },
  vidzeme: { blurb: 'Gaujas ielejas un pauguri' },
  latgale: { blurb: 'Zilo ezeru zeme' },
  zemgale: { blurb: 'Auglīgie lauki un līdzenumi' },
  selija: { blurb: 'Sēļu senā kultūrvēsture' },
};

const SUBTYPE_LV = { district: 'Mikrorajons', village: 'Ciems', parish: 'Pagasts' };
const JUMP_TYPE_LV = { city: 'Pilsēta', municipality: 'Novads', parish: 'Pagasts', district: 'Rajons', village: 'Ciems', region: 'Reģions' };

const GlassPanel = ({ children, panelKey }) => (
  <motion.div
    key={panelKey}
    initial={{ opacity: 0, y: 24, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -24, scale: 0.98 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10"
    style={{ fontFamily: 'Manrope, sans-serif' }}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-500/80 mb-3">{children}</p>
);

const BackBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-semibold mb-4 transition-colors"
    data-testid="welcome-back-btn"
  >
    <ArrowLeft className="w-3.5 h-3.5" /> Atpakaļ
  </button>
);

const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const SearchBox = ({ value, onChange, placeholder, testid }) => (
  <div className="relative mb-3">
    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-11 pl-10 pr-9 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:outline-none transition-colors"
      data-testid={testid}
    />
    {value && (
      <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

export default function WelcomeOnboarding() {
  const navigate = useNavigate();
  const { user, login, register, loginWithGoogle, setLanguagePref, updateProfile } = useAuth();
  const { setLanguage } = useLanguage();
  // When ?restart=1 (from Profile → "Restart onboarding"), skip the auto-redirect
  // for already-onboarded users and re-run the geo flow from the language step.
  const restart = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('restart') === '1';
  // auth | country | language | region | city | district | role | shoptype
  const [step, setStep] = useState('auth');
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [exchanging, setExchanging] = useState(false);

  // Country roadmap + invest chooser state
  const [countryModal, setCountryModal] = useState(null); // locked country object
  const [investTarget, setInvestTarget] = useState(null); // { campaign, name }
  const [roleChoice, setRoleChoice] = useState(null);      // 'seller' | 'service'

  // Geo state
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [selRegion, setSelRegion] = useState(null);
  const [selCity, setSelCity] = useState(null);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);

  // Quick "search & jump" — type any place and fly straight to it (option C).
  const [jumpQuery, setJumpQuery] = useState('');
  const [jumpResults, setJumpResults] = useState([]);
  const [jumpLoading, setJumpLoading] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const jumpDebounce = useRef(null);

  // Map
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapActive, setMapActive] = useState(false);

  const isGeo = step === 'region' || step === 'city' || step === 'district';

  // ---- Init map once (lives behind everything from the language step on) ----
  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: LATVIA_CENTER,
      zoom: 6.2,
      attributionControl: false,
      maxBounds: [[18.0, 54.5], [30.5, 59.5]],
    });
    mapRef.current = map;
    map.on('load', () => {
      map.fitBounds(LATVIA_BOUNDS, { padding: 40, duration: 0 });
      map.resize();
    });
    map.on('error', () => {});
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  const flyTo = useCallback((lng, lat, zoom) => {
    const map = mapRef.current;
    if (!map || lng == null || lat == null) return;
    map.flyTo({ center: [lng, lat], zoom, duration: 1400, essential: true });
    if (markerRef.current) markerRef.current.remove();
    const el = document.createElement('div');
    el.style.cssText = 'width:20px;height:20px;border-radius:50% 50% 50% 0;background:#FF9F1C;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 0 16px rgba(255,159,28,0.8)';
    markerRef.current = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
  }, []);

  // Handle Google OAuth return
  useEffect(() => {
    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    (async () => {
      setExchanging(true);
      const res = await loginWithGoogle(decodeURIComponent(m[1]));
      window.history.replaceState(null, '', window.location.pathname);
      setExchanging(false);
      if (res.success) {
        if (res.user?.region) navigate('/');
        else setStep('country');
      } else {
        toast.error(res.error || 'Google pieteikšanās neizdevās');
      }
    })();
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    if (user && !exchanging && step === 'auth') {
      if (user.region && !restart) navigate('/');
      else setStep('country');
    }
  }, [user, exchanging, step, navigate, restart]);

  // Reveal the map once we leave the auth screen.
  useEffect(() => {
    if (step !== 'auth') {
      setMapActive(true);
      setTimeout(() => { try { mapRef.current?.resize(); } catch (_) { /* noop */ } }, 350);
    }
  }, [step]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = mode === 'login'
      ? await login(form.email, form.password)
      : await register({ name: form.name, email: form.email, password: form.password });
    setBusy(false);
    if (res.success && res.requiresVerification) {
      toast.success('Pārbaudi e-pastu, lai apstiprinātu kontu!');
      setMode('login');
      return;
    }
    if (res.success) {
      toast.success(mode === 'login' ? 'Sveiks atpakaļ!' : 'Konts izveidots!');
      // Already-onboarded users (have a saved region) skip straight to the app,
      // unless they explicitly asked to restart onboarding (?restart=1).
      if (mode === 'login' && res.user?.region && !restart) navigate('/');
      else setStep('country');
    } else {
      toast.error(res.error || 'Neizdevās. Pārbaudi datus.');
    }
  };

  const chooseLanguage = async (lang) => {
    setLanguage(lang);
    await setLanguagePref(lang);
    goToRegion();
  };

  const goToRegion = async () => {
    setFilter('');
    setStep('region');
    mapRef.current?.fitBounds(LATVIA_BOUNDS, { padding: 40, duration: 1200 });
    if (regions.length) return;
    setLoadingGeo(true);
    try {
      const { data } = await axios.get(`${API}/onboarding/regions`);
      setRegions(data.regions || []);
    } catch (_) { toast.error('Neizdevās ielādēt reģionus'); }
    finally { setLoadingGeo(false); }
  };

  const chooseRegion = async (r) => {
    setSelRegion(r);
    setSelCity(null);
    setFilter('');
    flyTo(r.lng, r.lat, 8);
    setStep('city');
    setLoadingGeo(true);
    setCities([]);
    try {
      const { data } = await axios.get(`${API}/onboarding/cities`, { params: { region: r.slug } });
      setCities(data.cities || []);
    } catch (_) { toast.error('Neizdevās ielādēt pilsētas'); }
    finally { setLoadingGeo(false); }
  };

  const chooseCity = async (c) => {
    setSelCity(c);
    setFilter('');
    flyTo(c.lng, c.lat, 11);
    setStep('district');
    setLoadingGeo(true);
    setSubs([]);
    try {
      const { data } = await axios.get(`${API}/onboarding/sublocations`, { params: { slug: c.slug } });
      setSubs(data.sublocations || []);
    } catch (_) { /* sublocations are optional */ }
    finally { setLoadingGeo(false); }
  };

  const onJumpChange = (v) => {
    setJumpQuery(v);
    clearTimeout(jumpDebounce.current);
    if (v.trim().length < 2) { setJumpResults([]); setJumpOpen(false); return; }
    jumpDebounce.current = setTimeout(async () => {
      setJumpLoading(true);
      try {
        const { data } = await axios.get(`${API}/locations`, { params: { q: v.trim(), limit: 8 } });
        setJumpResults(data.locations || []);
        setJumpOpen(true);
      } catch (_) { setJumpResults([]); } finally { setJumpLoading(false); }
    }, 250);
  };

  // Resolve a searched place → its novads (region) + city, fly the map there and
  // land on the apkaime (district) step so the optional refinement still shows.
  const jumpToPlace = async (loc) => {
    setJumpOpen(false);
    setJumpQuery(loc.name);
    setLoadingGeo(true);
    setSubs([]);
    try {
      const { data } = await axios.get(`${API}/locations/resolve-region`, { params: { id: loc.id, slug: loc.slug } });
      const citySlug = data.city_slug || loc.slug;
      const cityName = data.city_name || loc.name;
      const cityLat = data.city_lat ?? loc.lat;
      const cityLng = data.city_lng ?? loc.lng;
      setSelRegion({ slug: data.region_slug || '', name: data.region_slug || '' });
      setSelCity({ slug: citySlug, name: cityName, lat: cityLat, lng: cityLng });
      flyTo(loc.lng ?? cityLng, loc.lat ?? cityLat, 11);
      setStep('district');
      // Prefill the filter when a precise sub-place was searched so it's one tap away.
      setFilter(['district', 'village', 'parish'].includes(loc.type) && loc.slug !== citySlug ? loc.name : '');
      try {
        const sres = await axios.get(`${API}/onboarding/sublocations`, { params: { slug: citySlug } });
        setSubs(sres.data.sublocations || []);
      } catch (_) { /* sublocations optional */ }
    } catch (_) {
      toast.error('Neizdevās atrast vietu');
    } finally { setLoadingGeo(false); }
  };

  const finish = async (sub) => {
    if (!selRegion || !selCity) return;
    setSaving(true);
    const payload = {
      region: selCity.slug,
      location_region_slug: selRegion.slug,
      location_name: sub ? sub.name : selCity.name,
      lat: sub ? sub.lat : selCity.lat,
      lng: sub ? sub.lng : selCity.lng,
    };
    if (sub) {
      payload.location_district = sub.name;
      payload.location_district_slug = sub.slug;
    }
    const res = await updateProfile(payload);
    setSaving(false);
    if (res.success) {
      toast.success('Apkaime saglabāta! Laipni lūdzam KoLV.');
      // Continue the roadmap: ask whether they want to sell / offer services.
      setStep('role');
    } else {
      toast.error(res.error || 'Neizdevās saglabāt atrašanās vietu');
    }
  };

  // Country roadmap: Latvia advances; locked countries open the invest chooser.
  const chooseCountry = (c) => {
    if (c.active) { setStep('language'); return; }
    setCountryModal(c);
  };

  // Role roadmap → shop-type chooser (or straight to the app when just browsing).
  const chooseRole = async (role) => {
    setRoleChoice(role);
    try { await updateProfile(role === 'service' ? { is_provider: true } : {}); } catch (_) { /* non-blocking */ }
    setStep('shoptype');
  };

  const chooseShopType = (type) => {
    navigate(type === 'business' ? '/business-templates' : '/shop-onboarding');
  };

  const field = (key, placeholder, Icon, type = 'text') => (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
      <input
        type={type}
        value={form[key]}
        onChange={(ev) => setForm({ ...form, [key]: ev.target.value })}
        placeholder={placeholder}
        required
        className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
        data-testid={`welcome-${key}-input`}
      />
    </div>
  );

  const filteredCities = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [cities, filter]);

  const filteredSubs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [subs, filter]);

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden" style={{ background: '#0A0D0B' }}>
      {/* Live map background (revealed from language step onward).
          MapLibre forces position:relative on its own container, so we mount it
          on an inner w-full/h-full div inside an absolute wrapper to keep size. */}
      <div
        className="absolute inset-0 z-0 transition-opacity duration-700"
        style={{ opacity: mapActive ? 1 : 0 }}
        data-testid="welcome-map"
      >
        <div ref={mapContainer} className="w-full h-full" />
      </div>
      {/* Cinematic still image for the auth step */}
      <AnimatePresence>
        {!mapActive && (
          <motion.div key="stillbg" exit={{ opacity: 0 }} className="absolute inset-0 z-[1]">
            <img src={BG} alt="" className="w-full h-full object-cover opacity-40 scale-105" />
          </motion.div>
        )}
      </AnimatePresence>
      {/* Readability vignette over the map */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none transition-opacity duration-700"
        style={{
          opacity: isGeo ? 0.85 : 1,
          background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.85))',
        }}
      />

      <div className="relative z-10 w-full px-5 flex justify-center">
        <AnimatePresence mode="wait">
          {exchanging ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-white/80">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-amber-500" />
              <p style={{ fontFamily: 'Manrope' }}>Pieslēdzas ar Google…</p>
            </motion.div>

          ) : step === 'auth' ? (
            <GlassPanel panelKey="auth">
              <Overline>KoLV · Baltijas kopiena</Overline>
              <h1 className="text-white text-4xl sm:text-5xl font-light tracking-tighter mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Laipni lūdzam<span className="text-amber-500">.</span>
              </h1>
              <p className="text-white/60 text-sm mb-8" style={{ fontFamily: 'Manrope' }}>
                {mode === 'login' ? 'Pieslēdzies un atrodi savu apkaimi.' : 'Izveido kontu dažu sekunžu laikā.'}
              </p>

              <GoogleAuthButton
                className="w-full h-12 rounded-full bg-white text-[#0A0D0B] font-semibold text-sm flex items-center justify-center gap-3 hover:bg-white/90 transition-colors"
                testid="welcome-google-btn"
                onDone={(u) => { if (u?.region && !restart) navigate('/'); else setStep('country'); }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                Turpināt ar Google
              </GoogleAuthButton>

              <div className="flex items-center gap-3 my-6">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-white/40 text-xs uppercase tracking-widest">vai</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <form onSubmit={submitEmail} className="space-y-3">
                {mode === 'register' && field('name', 'Vārds', User)}
                {field('email', 'E-pasts', Mail, 'email')}
                {field('password', 'Parole', Lock, 'password')}
                {mode === 'login' && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs text-amber-500 font-semibold hover:underline"
                      data-testid="welcome-forgot-password"
                    >
                      Aizmirsi paroli?
                    </button>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60"
                  style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
                  data-testid="welcome-submit-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === 'login' ? 'Pieslēgties' : 'Reģistrēties'} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>

              <p className="text-center text-white/50 text-sm mt-6" style={{ fontFamily: 'Manrope' }}>
                {mode === 'login' ? 'Nav konta?' : 'Jau ir konts?'}{' '}
                <button
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="text-amber-500 font-semibold hover:underline"
                  data-testid="welcome-toggle-mode"
                >
                  {mode === 'login' ? 'Izveido' : 'Pieslēdzies'}
                </button>
              </p>
            </GlassPanel>

          ) : step === 'country' ? (
            <GlassPanel panelKey="country">
              <Overline>Solis 1 · Valsts</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Izvēlies valsti
              </h2>
              <p className="text-white/60 text-sm mb-6" style={{ fontFamily: 'Manrope' }}>
                Latvija jau ir šeit. Pārējās vēl atveram — palīdzi tām pievienoties!
              </p>
              <motion.div variants={listVariants} initial="hidden" animate="show" className="grid grid-cols-2 gap-3">
                {COUNTRIES.map((c) => (
                  <motion.button
                    key={c.code}
                    variants={itemVariants}
                    onClick={() => chooseCountry(c)}
                    className={`relative flex flex-col items-start gap-1 p-4 rounded-2xl border text-left transition-colors group overflow-hidden ${
                      c.active
                        ? 'border-2 border-amber-500 bg-amber-500/10 text-white hover:bg-amber-500/20'
                        : 'border-white/10 bg-white/5 text-white/50 hover:bg-white/10 hover:border-white/20'
                    }`}
                    data-testid={c.active ? `country-select-${c.code}` : `country-select-locked-${c.code}`}
                  >
                    {!c.active && (
                      <span className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center" title="Vēl nav pieejama — atbalsti atvēršanu">
                        <Euro className="w-3.5 h-3.5 text-amber-400" />
                      </span>
                    )}
                    <span className={`text-2xl leading-none ${c.active ? '' : 'opacity-60 grayscale group-hover:grayscale-0 transition-[filter]'}`}>{c.flag}</span>
                    <span className="font-semibold" style={{ fontFamily: 'Manrope' }}>{c.name}</span>
                    <span className={`text-[11px] ${c.active ? 'text-amber-300/90' : 'text-white/40'}`}>
                      {c.active ? 'Pieejama' : 'Drīzumā'}
                    </span>
                  </motion.button>
                ))}
              </motion.div>
              <p className="text-white/35 text-xs mt-5 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <MapPin className="w-3.5 h-3.5" /> Nākamais: valoda un tava apkaime.
              </p>
            </GlassPanel>

          ) : step === 'language' ? (
            <GlassPanel panelKey="language">
              <BackBtn onClick={() => setStep('country')} />
              <Overline>Solis 2 · Valoda</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Izvēlies valodu
              </h2>
              <p className="text-white/60 text-sm mb-8" style={{ fontFamily: 'Manrope' }}>
                Aplikācija turpināsies izvēlētajā valodā.
              </p>
              <div className="space-y-3">
                {[{ c: 'lv', flag: '🇱🇻', label: 'Latviešu' }, { c: 'ru', flag: '🇷🇺', label: 'Русский' }, { c: 'en', flag: '🇬🇧', label: 'English' }].map((l) => (
                  <button
                    key={l.c}
                    onClick={() => chooseLanguage(l.c)}
                    className="w-full h-14 rounded-2xl bg-white/5 border border-white/10 text-white flex items-center gap-4 px-5 hover:border-amber-500/60 hover:bg-white/[0.08] transition-colors group"
                    data-testid={`welcome-lang-${l.c}`}
                  >
                    <span className="text-2xl">{l.flag}</span>
                    <span className="font-semibold flex-1 text-left" style={{ fontFamily: 'Manrope' }}>{l.label}</span>
                    <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
              <p className="text-white/35 text-xs mt-6 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <MapPin className="w-3.5 h-3.5" /> Nākamais: atrodi savu apkaimi kartē.
              </p>
            </GlassPanel>

          ) : step === 'region' ? (
            <GlassPanel panelKey="region">
              <BackBtn onClick={() => setStep('language')} />
              <Overline>Solis 3 · Reģions</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Kurā novadā dzīvo?
              </h2>
              <p className="text-white/60 text-sm mb-6" style={{ fontFamily: 'Manrope' }}>
                Izvēlies savu vēsturisko reģionu — karte aizlidos turp.
              </p>

              {/* Quick search & jump (option C) */}
              <div className="relative mb-5" data-testid="welcome-jump">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  value={jumpQuery}
                  onChange={(e) => onJumpChange(e.target.value)}
                  onFocus={() => jumpResults.length && setJumpOpen(true)}
                  placeholder="Meklē pilsētu, ciemu vai rajonu un uzlec uzreiz…"
                  className="w-full h-11 pl-10 pr-9 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:outline-none transition-colors"
                  data-testid="welcome-jump-input"
                />
                {jumpLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500 animate-spin" />}
                {jumpQuery && !jumpLoading && (
                  <button onClick={() => { setJumpQuery(''); setJumpResults([]); setJumpOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" data-testid="welcome-jump-clear">
                    <X className="w-4 h-4" />
                  </button>
                )}
                {jumpOpen && jumpResults.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full rounded-2xl border border-white/10 bg-[#0A0D0B]/95 backdrop-blur-xl shadow-2xl max-h-64 overflow-auto" data-testid="welcome-jump-results">
                    {jumpResults.map((loc) => (
                      <button
                        key={loc.id}
                        onClick={() => jumpToPlace(loc)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/10 transition-colors"
                        data-testid={`welcome-jump-option-${loc.slug}`}
                      >
                        <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                        <span className="text-sm text-white flex-1 min-w-0 truncate" style={{ fontFamily: 'Manrope' }}>{loc.name}</span>
                        <span className="text-[11px] text-white/40 shrink-0">{JUMP_TYPE_LV[loc.type] || loc.type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-white/40 text-[11px] uppercase tracking-widest">vai izvēlies reģionu</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              {loadingGeo && !regions.length ? (
                <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
              ) : (
                <motion.div variants={listVariants} initial="hidden" animate="show" className="space-y-2.5">
                  {regions.map((r) => (
                    <motion.button
                      key={r.slug}
                      variants={itemVariants}
                      onClick={() => chooseRegion(r)}
                      className="w-full rounded-2xl bg-white/5 border border-white/10 text-white flex items-center gap-4 px-5 py-3.5 hover:border-amber-500/60 hover:bg-white/[0.08] transition-colors group text-left"
                      data-testid={`welcome-region-${r.slug}`}
                    >
                      <span className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                        <Trees className="w-4 h-4 text-amber-400" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold" style={{ fontFamily: 'Manrope' }}>{r.name}</span>
                        <span className="block text-white/45 text-xs truncate">{REGION_META[r.slug]?.blurb} · {r.city_count} pilsētas</span>
                      </span>
                      <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </GlassPanel>

          ) : step === 'city' ? (
            <GlassPanel panelKey="city">
              <BackBtn onClick={() => { setStep('region'); }} />
              <Overline>Solis 4 · {selRegion?.name}</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Pilsēta vai novads
              </h2>
              <SearchBox value={filter} onChange={setFilter} placeholder="Meklēt pilsētu…" testid="welcome-city-search" />
              {loadingGeo && !cities.length ? (
                <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
              ) : (
                <div className="max-h-[46vh] overflow-y-auto pr-1 -mr-1 space-y-2" data-testid="welcome-city-list">
                  {filteredCities.map((c) => (
                    <button
                      key={c.slug}
                      onClick={() => chooseCity(c)}
                      className="w-full rounded-2xl bg-white/5 border border-white/10 text-white flex items-center gap-3.5 px-4 py-3 hover:border-amber-500/60 hover:bg-white/[0.08] transition-colors group text-left"
                      data-testid={`welcome-city-${c.slug}`}
                    >
                      <Building2 className="w-4 h-4 text-amber-400/80 shrink-0" />
                      <span className="flex-1 min-w-0 font-medium truncate" style={{ fontFamily: 'Manrope' }}>{c.name}</span>
                      {c.population > 0 && <span className="text-white/35 text-xs shrink-0">{c.population.toLocaleString('lv-LV')}</span>}
                      <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  ))}
                  {!filteredCities.length && <p className="text-white/40 text-sm py-4 text-center">Nekas nav atrasts</p>}
                </div>
              )}
            </GlassPanel>

          ) : step === 'district' ? (
            <GlassPanel panelKey="district">
              <BackBtn onClick={() => { setStep('city'); setFilter(''); }} />
              <Overline>Solis 5 · {selCity?.name}</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Tava apkaime
              </h2>
              <p className="text-white/55 text-sm mb-4" style={{ fontFamily: 'Manrope' }}>
                Izvēlies mikrorajonu, ciemu vai pagastu — vai turpini ar pilsētu.
              </p>

              <button
                onClick={() => finish(null)}
                disabled={saving}
                className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60 mb-4"
                style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
                data-testid="welcome-finish-city-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Turpināt ar {selCity?.name}</>}
              </button>

              {subs.length > 0 && (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="text-white/40 text-[11px] uppercase tracking-widest">vai precīzāk</span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                  <SearchBox value={filter} onChange={setFilter} placeholder="Meklēt apkaimi…" testid="welcome-sub-search" />
                </>
              )}

              {loadingGeo ? (
                <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
              ) : subs.length > 0 ? (
                <div className="max-h-[38vh] overflow-y-auto pr-1 -mr-1 space-y-2" data-testid="welcome-sub-list">
                  {filteredSubs.map((s) => (
                    <button
                      key={`${s.type}-${s.slug}`}
                      onClick={() => finish(s)}
                      disabled={saving}
                      className="w-full rounded-2xl bg-white/5 border border-white/10 text-white flex items-center gap-3.5 px-4 py-3 hover:border-amber-500/60 hover:bg-white/[0.08] transition-colors group text-left disabled:opacity-60"
                      data-testid={`welcome-sub-${s.slug}`}
                    >
                      <Compass className="w-4 h-4 text-amber-400/80 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium truncate" style={{ fontFamily: 'Manrope' }}>{s.name}</span>
                        <span className="block text-white/40 text-xs">{SUBTYPE_LV[s.type] || s.type} · {s.distance_km} km</span>
                      </span>
                      <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  ))}
                  {!filteredSubs.length && <p className="text-white/40 text-sm py-4 text-center">Nekas nav atrasts</p>}
                </div>
              ) : (
                <p className="text-white/40 text-sm py-2 text-center">Šai pilsētai nav sīkāku apkaimju.</p>
              )}
            </GlassPanel>

          ) : step === 'role' ? (
            <GlassPanel panelKey="role">
              <Overline>Gandrīz gatavs!</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Vai vēlies pārdot vai piedāvāt pakalpojumus?
              </h2>
              <p className="text-white/60 text-sm mb-7" style={{ fontFamily: 'Manrope' }}>
                Palīdzēsim tev atvērt savu veikalu dažu soļu laikā.
              </p>
              <div className="space-y-3">
                {[
                  { id: 'seller', icon: ShoppingBag, title: 'Esmu pārdevējs', desc: 'Vēlos atvērt veikalu un tirgot preces' },
                  { id: 'service', icon: Briefcase, title: 'Esmu pakalpojumu sniedzējs', desc: 'Piedāvāju pakalpojumus vai servisu' },
                ].map((o) => {
                  const Icon = o.icon;
                  return (
                    <button
                      key={o.id}
                      onClick={() => chooseRole(o.id)}
                      className="w-full flex items-center gap-4 p-5 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/[0.09] hover:border-amber-500/50 transition-colors text-left group"
                      data-testid={`role-selector-${o.id}`}
                    >
                      <span className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center text-amber-400 shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{o.title}</span>
                        <span className="block text-white/50 text-xs mt-0.5">{o.desc}</span>
                      </span>
                      <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => navigate('/')}
                className="mt-3 w-full flex items-center gap-4 p-5 rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.07] to-transparent hover:from-amber-500/10 hover:border-amber-500/40 transition-colors text-left group"
                data-testid="role-selector-skip"
              >
                <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-white/70 group-hover:text-amber-400 group-hover:bg-amber-500/15 transition-colors shrink-0">
                  <Compass className="w-5 h-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-white" style={{ fontFamily: 'Manrope' }}>Vispirms izpētīšu KoLV</span>
                  <span className="block text-white/50 text-xs mt-0.5">Ienirsti tirgū — veikalu varēsi atvērt jebkurā brīdī</span>
                </span>
                <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            </GlassPanel>

          ) : step === 'shoptype' ? (
            <GlassPanel panelKey="shoptype">
              <BackBtn onClick={() => setStep('role')} />
              <Overline>Atver savu veikalu</Overline>
              <h2 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Kāda veida veikalu?
              </h2>
              <p className="text-white/60 text-sm mb-7" style={{ fontFamily: 'Manrope' }}>
                Otru veidu varēsi pievienot vēlāk.
              </p>
              <ShopTypeChooser theme="dark" onPick={chooseShopType} />
            </GlassPanel>

          ) : (
            <GlassPanel panelKey="done">
              <div className="py-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" />
              </div>
            </GlassPanel>
          )}
        </AnimatePresence>
      </div>

      <CountryInvestModal
        country={countryModal}
        onClose={() => setCountryModal(null)}
        onPick={(campaign, name) => { setCountryModal(null); setInvestTarget({ campaign, name }); }}
      />
      <InvestModal target={investTarget} onClose={() => setInvestTarget(null)} />
    </div>
  );
}
